import React, { useState, useCallback, useEffect, useMemo, useRef, useImperativeHandle } from 'react';
import {
  Form, FormLayout, TextField, Select, InlineStack,
  Text, Box, Button, Card, ChoiceList, RadioButton, Popover, ActionList,
  Modal, Spinner, InlineError
} from '@shopify/polaris';
import { GlobeIcon, PlusCircleIcon, DeleteIcon } from '@shopify/polaris-icons';
import { PRICE_TYPES, ICON_CHOICES, PLAN_ICON_IMAGES, iconKey, resolvePlanIcon, isIconUrl } from '../constants';
import { PRESET_LANGUAGE_NAMES, presetForLocale, hasPreset } from '../i18n/planPresets';
import { useI18n, t as translate } from '../i18n';

// 类型选项的文案走词典;value 与逻辑不变。TYPE_CHOICES/TYPE_DEFAULTS 原是
// 模块级常量,为跟随 admin 语言改为按需构造(词典 key 见 locales/en.js)。
const typeChoices = (t) => [
  {
    value: 'shipping_protection',
    label: t('form.type.shipping_protection'),
    helpText: t('form.type.shipping_protection.help')
  },
  {
    value: 'warranty',
    label: t('form.type.warranty'),
    helpText: t('form.type.warranty.help')
  },
  {
    value: 'return_insurance',
    label: t('form.type.return_insurance'),
    helpText: t('form.type.return_insurance.help')
  },
  {
    value: 'custom',
    label: t('form.type.custom'),
    helpText: t('form.type.custom.help')
  }
];

// Sensible per-type defaults so a new plan starts mostly filled in, and
// switching type swaps the copy (only while the fields still hold defaults).
// 按当前 admin 语言取默认文案;比较逻辑(handleTypeChange)在运行时同样
// 用同语言的默认值做对照,切换语言后新建/换类型依然一致。
const typeDefaults = (t) => ({
  shipping_protection: {
    displayName: t('form.defaults.shipping_protection.displayName'),
    description: t('form.defaults.shipping_protection.description'),
    priceValue: '2'
  },
  warranty: {
    displayName: t('form.defaults.warranty.displayName'),
    description: t('form.defaults.warranty.description'),
    priceValue: '2',
    warrantyYears: '2'
  },
  return_insurance: {
    displayName: t('form.defaults.return_insurance.displayName'),
    description: t('form.defaults.return_insurance.description'),
    priceValue: '2',
    returnDays: '14'
  },
  custom: {
    displayName: t('form.defaults.custom.displayName'),
    description: t('form.defaults.custom.description'),
    priceValue: '2'
  }
});

// Percentage pricing defaults: the hidden plan product is generated with
// 200 fee rungs starting at the minimum price, stepping by the increment —
// each variant is priced AT its rung. At checkout the fee target is the
// percentage of the cart subtotal and the first rung at or above it applies
// (cart $100 at 2% targets $2.00 → the $3.01 rung). The maximum rung is
// derived (min + 199 × increment).
const PCT_DEFAULTS = { minCharge: '1.01', incrementAmount: '2' };
const PCT_BUCKETS = 200;

function formatMoneyInput(value) {
  const n = parseFloat(value);
  return Number.isNaN(n) ? '0.00' : n.toFixed(2);
}

// Fields whose content varies per language.
const TRANSLATABLE_FIELDS = ['displayName', 'description', 'disabledDescription', 'footerText'];

function toNumberOr(value, fallback = null) {
  if (value === '' || value == null) return fallback;
  const n = parseFloat(value);
  return Number.isNaN(n) ? fallback : n;
}

// pricingTiers 统一成后端回读的规范形:高级设置弹窗暂存的是文本输入的字符
// 串([{"min":"250","max":"","price":"10"}]),而后端把 tier 落到 PlanPrice
// 行,读取时按数字重新合成 JSON([{"min":250,"max":null,"price":10}])。提
// 交载荷若不做同样规整,保存响应重建的 savedPayload 与 formPayload 永远不
// 等,保存成功后顶栏会继续显示"未保存"。无有效价格的条目按后端规则丢弃;
// 解析失败时原样返回(交给后端校验报错)。
function canonicalTiers(raw) {
  let arr;
  try {
    arr = JSON.parse(raw || '[]');
  } catch {
    return raw || null;
  }
  if (!Array.isArray(arr)) return raw;
  const tiers = [];
  for (const t of arr) {
    const price = toNumberOr(t.price);
    if (price == null) continue;
    tiers.push({
      min: toNumberOr(t.min),
      max: toNumberOr(t.max),
      price
    });
  }
  return JSON.stringify(tiers);
}

function parseTranslations(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function initialFormState(plan) {
  // New plan: prefill sensible defaults for the default type so merchants
  // only need to tweak what they want. 走模块级 translate:本函数在订阅了
  // i18n 上下文的组件渲染过程中调用,取到的总是当前 admin 语言的默认文案。
  const defaults = plan ? {} : {
    name: translate('form.defaults.untitledPlan'),
    displayName: translate('form.defaults.shipping_protection.displayName'),
    description: translate('form.defaults.shipping_protection.description'),
    priceValue: typeDefaults(translate).shipping_protection.priceValue
  };
  return {
    name: plan?.name || defaults.name || '',
    displayName: plan?.displayName || defaults.displayName || '',
    description: plan?.displayDescription || plan?.description || defaults.description || '',
    disabledDescription: plan?.disabledDescription || '',
    type: plan?.type || 'shipping_protection',
    priceType: plan?.priceType || 'fixed',
    priceValue: plan?.priceValue != null ? String(plan.priceValue) : (defaults.priceValue || ''),
    minPrice: plan?.minPrice != null ? String(plan.minPrice) : '',
    maxPrice: plan?.maxPrice != null ? String(plan.maxPrice) : '',
    minCharge: plan?.minCharge != null ? String(plan.minCharge) : '',
    incrementAmount: plan?.incrementAmount != null ? String(plan.incrementAmount) : '',
    pricingTiers: plan?.pricingTiers || '[]',
    icon: resolvePlanIcon(plan?.icon) || '🛡️',
    isOptional: plan?.isOptional ?? true,
    defaultEnabled: plan?.defaultEnabled ?? false,
    warrantyYears: plan?.warrantyYears != null ? String(plan.warrantyYears) : '',
    returnDays: plan?.returnDays != null ? String(plan.returnDays) : '',
    variantId: plan?.variantId || '',
    sortOrder: plan?.sortOrder != null ? String(plan.sortOrder) : '0',
    fulfillment: plan?.fulfillment || 'manually',
    footerEnabled: plan?.footerEnabled ?? false,
    footerText: plan?.footerText || translate('form.defaults.viewTerms'),
    footerLink: plan?.footerLink || '',
    translations: parseTranslations(plan?.translations)
  };
}

// isActive lives outside the form (rendered as the "Plan status" card), so it
// is passed in explicitly when building the API payload.
export function formToPayload(formData, isActive = true) {
  const customerDescription = formData.description.trim() || null;
  const disabledDescription = formData.disabledDescription.trim() || null;
  // Drop empty translation entries before persisting.
  const cleanTranslations = {};
  Object.entries(formData.translations || {}).forEach(([locale, fields]) => {
    const clean = {};
    Object.entries(fields || {}).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== '') clean[key] = String(value);
    });
    if (Object.keys(clean).length > 0) cleanTranslations[locale] = clean;
  });
  return {
    name: formData.name.trim(),
    displayName: formData.displayName.trim(),
    description: customerDescription,
    displayDescription: customerDescription,
    disabledDescription,
    type: formData.type,
    priceType: formData.priceType,
    priceValue: toNumberOr(formData.priceValue, 0),
    minPrice: toNumberOr(formData.minPrice),
    maxPrice: toNumberOr(formData.maxPrice),
    minCharge: formData.priceType === 'percentage' ? toNumberOr(formData.minCharge) : null,
    incrementAmount: formData.priceType === 'percentage' ? toNumberOr(formData.incrementAmount) : null,
    pricingTiers: formData.priceType === 'fixed' && formData.pricingTiers && formData.pricingTiers !== '[]'
      ? canonicalTiers(formData.pricingTiers)
      : null,
    icon: formData.icon || null,
    isOptional: formData.isOptional,
    defaultEnabled: Boolean(formData.defaultEnabled),
    isActive,
    warrantyYears: formData.type === 'warranty' ? toNumberOr(formData.warrantyYears) : null,
    returnDays: formData.type === 'return_insurance' ? toNumberOr(formData.returnDays) : null,
    variantId: formData.variantId.trim() || null,
    sortOrder: toNumberOr(formData.sortOrder, 0),
    fulfillment: formData.fulfillment || 'manually',
    footerEnabled: formData.footerEnabled,
    footerText: formData.footerText.trim() || translate('form.defaults.viewTerms'),
    footerLink: formData.footerLink.trim() || null,
    translations: Object.keys(cleanTranslations).length > 0
      ? JSON.stringify(cleanTranslations)
      : null
  };
}

function SectionCard({ title, actions, children }) {
  return (
    <Card>
      <Box padding="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingSm" as="h2">{title}</Text>
          {actions}
        </InlineStack>
        <Box paddingBlockStart="300">
          <FormLayout>
            {children}
          </FormLayout>
        </Box>
      </Box>
    </Card>
  );
}

// Icon picker tiles: the button is exactly 80×80 (merchant-checked in
// devtools), the image 64×64 inside the 8px padding.
const ICON_TILE_SIZE = 80;
const ICON_IMAGE_SIZE = 64;
// Mirror of the backend's upload cap (internal/api/upload.go maxUploadBytes).
const ICON_MAX_BYTES = 5 * 1024 * 1024;

function iconTileStyle(selected) {
  return {
    width: ICON_TILE_SIZE,
    height: ICON_TILE_SIZE,
    padding: 8,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: selected ? '#F1F1F1' : '#FFFFFF',
    border: `1px solid ${selected ? '#8A8A8A' : '#D0D0D0'}`,
    borderRadius: 8,
    boxShadow: selected ? 'inset 0 0 0 1px #1A1A1A' : 'none',
    cursor: 'pointer',
    fontFamily: 'inherit'
  };
}

const ProtectionPlanForm = React.forwardRef(function ProtectionPlanForm(
  { plan, isActive = true, onSave, onDraftChange, locales, uploadFile },
  ref
) {
  const { t, tp } = useI18n();
  const [formData, setFormData] = useState(() => initialFormState(plan));
  const [errors, setErrors] = useState({});
  const [lang, setLang] = useState('default');
  const [langOpen, setLangOpen] = useState(false);

  // Custom icon upload state: the picker's dashed tile funnels one image
  // file through the backend (POST /api/uploads → R2) and stores the
  // returned URL as the plan's icon, next to the emoji values.
  const iconFileRef = useRef(null);
  const [iconUploading, setIconUploading] = useState(false);
  const [iconUploadError, setIconUploadError] = useState(null);

  // 语言切换器:第一项是店铺主语言(表单主字段存的就是它的内容),其余
  // 项来自店铺的 published locale(GET /api/locales 实时读取 Shopify 店
  // 铺语言设置)。店铺语言读不到(未装好 / Shopify 不可达 / 本地演示)时
  // 退回预设语言列表,切换器保持可用。
  const primaryLanguageName = useMemo(
    () => locales?.find(l => l.primary)?.name || 'English',
    [locales]
  );
  const LANGUAGES = useMemo(() => {
    const list = [{ value: 'default', label: t('form.lang.default', { name: primaryLanguageName }) }];
    const published = (locales || []).filter(l => l.published && !l.primary);
    if (published.length === 0) {
      return list.concat(
        Object.entries(PRESET_LANGUAGE_NAMES).map(([value, label]) => ({ value, label }))
      );
    }
    const seen = new Set(['default']);
    for (const l of published) {
      if (seen.has(l.locale)) continue;
      seen.add(l.locale);
      list.push({ value: l.locale, label: l.name || l.locale });
    }
    return list;
  }, [locales, primaryLanguageName, t]);

  // 切语言时,该语言还没有任何译文就立刻用系统预设内容填上(仅预设语言,
  // 按当前计划类型取对应文案),商户在此基础上微调后保存。不在预设里的语
  // 言保持留空:c 端渲染会回退到主语言(英语)内容。
  const switchLang = useCallback((next) => {
    setLang(next);
    if (next === 'default') return;
    setFormData(prev => {
      const existing = prev.translations[next];
      if (existing && TRANSLATABLE_FIELDS.some(f => (existing[f] || '').trim() !== '')) return prev;
      const preset = presetForLocale(next, prev.type);
      if (!preset) return prev;
      return {
        ...prev,
        translations: {
          ...prev.translations,
          [next]: { ...(prev.translations[next] || {}), ...preset }
        }
      };
    });
  }, []);
  // Advanced pricing modals — edits are staged locally and only committed to
  // the form state when the merchant presses Save.
  const [advOpen, setAdvOpen] = useState(false);
  const [stagedAdv, setStagedAdv] = useState({ minCharge: '', incrementAmount: '' });
  const [tiersOpen, setTiersOpen] = useState(false);
  const [stagedTiers, setStagedTiers] = useState([]);

  const handleChange = useCallback((field) => (value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  }, []);

  const pickIconFile = useCallback(() => {
    setIconUploadError(null);
    iconFileRef.current?.click();
  }, []);

  const onIconFileChosen = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // re-picking the same file after a fix must fire change
    if (!file || !uploadFile) return;
    if (!/^image\//.test(file.type)) {
      setIconUploadError(t('form.icon.error.notImage'));
      return;
    }
    if (file.size > ICON_MAX_BYTES) {
      setIconUploadError(t('form.icon.error.tooLarge'));
      return;
    }
    setIconUploading(true);
    try {
      const url = await uploadFile(file);
      setIconUploadError(null);
      handleChange('icon')(url);
    } catch (err) {
      setIconUploadError(err.message);
    } finally {
      setIconUploading(false);
    }
  }, [uploadFile, handleChange, t]);

  // Back to the default emoji when a merchant removes their custom icon.
  const removeCustomIcon = useCallback(() => {
    handleChange('icon')('🛡️');
  }, [handleChange]);

  // ---- Advanced pricing modals ----
  const openAdvModal = useCallback(() => {
    setStagedAdv({
      minCharge: formData.minCharge || PCT_DEFAULTS.minCharge,
      incrementAmount: formData.incrementAmount || PCT_DEFAULTS.incrementAmount
    });
    setAdvOpen(true);
  }, [formData.minCharge, formData.incrementAmount]);

  const saveAdvModal = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      minCharge: stagedAdv.minCharge,
      incrementAmount: stagedAdv.incrementAmount
    }));
    setAdvOpen(false);
  }, [stagedAdv]);

  const advMin = toNumberOr(stagedAdv.minCharge);
  const advInc = toNumberOr(stagedAdv.incrementAmount);
  const advValid = advMin != null && advMin > 0 && advInc != null && advInc > 0;
  // Derived top of the price ladder: the minimum amount takes bucket #0 and
  // the remaining 249 buckets step by the increment.
  const advMax = advMin != null && advInc != null
    ? (advMin + advInc * (PCT_BUCKETS - 1)).toFixed(2)
    : null;
  const advPct = toNumberOr(formData.priceValue);

  const openTiersModal = useCallback(() => {
    try {
      const arr = JSON.parse(formData.pricingTiers || '[]');
      setStagedTiers(Array.isArray(arr) ? arr.map(t => ({
        min: t.min != null ? String(t.min) : '',
        max: t.max != null ? String(t.max) : '',
        price: t.price != null ? String(t.price) : ''
      })) : []);
    } catch {
      setStagedTiers([]);
    }
    setTiersOpen(true);
  }, [formData.pricingTiers]);

  // Every tier needs a min, a max and a price, and min ≤ max — the plan
  // product generates one variant per rule, so partial rules are rejected.
  // Ranges may overlap.
  const tierErrors = useMemo(() => {
    const errs = {};
    stagedTiers.forEach((tier, index) => {
      const min = toNumberOr(tier.min);
      const max = toNumberOr(tier.max);
      const price = toNumberOr(tier.price);
      if (min == null) errs[index] = t('form.tiers.error.minRequired');
      else if (max == null) errs[index] = t('form.tiers.error.maxRequired');
      else if (price == null) errs[index] = t('form.tiers.error.priceRequired');
      else if (price <= 0) errs[index] = t('form.tiers.error.pricePositive');
      else if (min > max) errs[index] = t('form.tiers.error.minMax');
    });
    return errs;
  }, [stagedTiers, t]);
  const tiersValid = stagedTiers.every((_, index) => !tierErrors[index]);

  const saveTiersModal = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: JSON.stringify(stagedTiers.filter(t => t.min !== '' || t.max !== '' || t.price !== ''))
    }));
    setTiersOpen(false);
  }, [stagedTiers]);

  const updateTier = useCallback((index, key, value) => {
    setStagedTiers(prev => prev.map((t, i) => (i === index ? { ...t, [key]: value } : t)));
  }, []);

  const addTier = useCallback(() => {
    setStagedTiers(prev => [...prev, { min: '', max: '', price: '' }]);
  }, []);

  const removeTier = useCallback((index) => {
    setStagedTiers(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Switching type swaps the display copy and price, but only while those
  // fields are untouched (empty or still holding another type's default), so
  // merchant-entered text is never overwritten. Dedicated fields (warranty
  // years / return days) get a starting value when empty.
  const handleTypeChange = useCallback((nextType) => {
    setFormData(prev => {
      const allDefaults = Object.values(typeDefaults(translate));
      const nextDefault = typeDefaults(translate)[nextType] || {};
      const patch = { type: nextType };
      if (!prev.displayName || allDefaults.some(d => d.displayName === prev.displayName)) {
        patch.displayName = nextDefault.displayName || '';
      }
      if (!prev.description || allDefaults.some(d => d.description === prev.description)) {
        patch.description = nextDefault.description || '';
      }
      if (!prev.priceValue || allDefaults.some(d => d.priceValue === prev.priceValue)) {
        patch.priceValue = nextDefault.priceValue || '';
      }
      if (nextType === 'warranty' && !prev.warrantyYears) {
        patch.warrantyYears = nextDefault.warrantyYears || '';
      }
      if (nextType === 'return_insurance' && !prev.returnDays) {
        patch.returnDays = nextDefault.returnDays || '';
      }
      // 预设译文跟随类型切换:当前语言的字段仍是旧类型预设原文(或为空)
      // 时换成新类型预设,商户改过的译文绝不动。
      let translations = prev.translations;
      if (lang !== 'default' && translations[lang]) {
        const oldPreset = presetForLocale(lang, prev.type);
        const nextPreset = presetForLocale(lang, nextType);
        if (nextPreset) {
          const updated = { ...translations[lang] };
          let touched = false;
          for (const f of ['displayName', 'description', 'disabledDescription']) {
            if (!updated[f] || updated[f] === oldPreset?.[f]) {
              updated[f] = nextPreset[f];
              touched = true;
            }
          }
          if (touched) translations = { ...translations, [lang]: updated };
        }
      }
      return { ...prev, ...patch, translations };
    });
    setErrors({});
  }, [lang]);

  // Read/write a field in the currently selected language. The default
  // language maps to the main columns; others live in formData.translations.
  const getTranslated = useCallback((field) => {
    if (lang === 'default') return formData[field] ?? '';
    const entry = formData.translations[lang];
    return (entry && entry[field]) || '';
  }, [lang, formData]);

  const setTranslated = useCallback((field, value) => {
    setFormData(prev => {
      if (lang === 'default') return { ...prev, [field]: value };
      return {
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            ...(prev.translations[lang] || {}),
            [field]: value
          }
        }
      };
    });
    setErrors(prev => ({ ...prev, [field]: undefined }));
  }, [lang]);

  // Stream the current draft up so the preview panel can simulate live edits.
  useEffect(() => {
    if (onDraftChange) onDraftChange(formData);
  }, [formData, onDraftChange]);

  const validate = useCallback(() => {
    const next = {};
    if (!formData.name.trim()) next.name = t('form.field.nameRequired');
    if (!formData.displayName.trim()) next.displayName = t('form.field.displayNameRequired');

    const price = toNumberOr(formData.priceValue);
    if (price == null || price <= 0) {
      next.priceValue = t('form.validation.priceRequired');
    } else if (formData.priceType === 'percentage' && price > 100) {
      next.priceValue = t('form.validation.priceMax100');
    }

    if (formData.type === 'warranty') {
      const years = toNumberOr(formData.warrantyYears);
      if (years == null || years < 1 || years > 5) {
        next.warrantyYears = t('form.validation.warrantyYears');
      }
    }
    if (formData.type === 'return_insurance') {
      const days = toNumberOr(formData.returnDays);
      if (days == null || days < 7 || days > 365) {
        next.returnDays = t('form.validation.returnDays');
      }
    }

    const min = toNumberOr(formData.minPrice);
    const max = toNumberOr(formData.maxPrice);
    if (min != null && max != null && min > max) {
      next.maxPrice = t('form.validation.maxGreaterThanMin');
    }

    const variantId = formData.variantId.trim();
    if (variantId && !/^\d+$/.test(variantId)) {
      next.variantId = t('form.validation.variantIdNumeric');
    }

    return next;
  }, [formData, t]);

  const submit = useCallback(() => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    // isActive 由页面状态卡控制,随载荷一起提交(否则保存会把计划误激活)
    onSave(formToPayload(formData, isActive));
  }, [validate, formData, isActive, onSave]);

  useImperativeHandle(ref, () => ({ submit }));

  const showWarrantyFields = formData.type === 'warranty';
  const showReturnFields = formData.type === 'return_insurance';
  const langLabel = LANGUAGES.find(l => l.value === lang)?.label
    || t('form.lang.default', { name: primaryLanguageName });

  return (
    <Form onSubmit={submit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <SectionCard title={t('form.section.type')}>
          <ChoiceList
            title=""
            titleHidden
            name="protectionType"
            choices={typeChoices(t).map(c => ({
              value: c.value,
              label: c.label,
              helpText: c.helpText
            }))}
            selected={[formData.type]}
            onChange={(selected) => handleTypeChange(selected[0])}
          />
        </SectionCard>

        <SectionCard
          title={t('form.section.basics')}
          actions={
            <Popover
              active={langOpen}
              activator={
                <Button
                  icon={GlobeIcon}
                  onClick={() => setLangOpen(o => !o)}
                  accessibilityLabel={t('form.lang.select')}
                >
                  {langLabel}
                </Button>
              }
              onClose={() => setLangOpen(false)}
            >
              <ActionList
                items={LANGUAGES.map(l => ({
                  content: l.label,
                  active: l.value === lang,
                  onAction: () => { switchLang(l.value); setLangOpen(false); }
                }))}
              />
            </Popover>
          }
        >
          <TextField
            label={t('form.field.planName.label')}
            value={formData.name}
            onChange={handleChange('name')}
            autoComplete="off"
            helpText={t('form.field.planName.help')}
            error={errors.name}
            requiredIndicator
          />

          <Box paddingBlockStart="200">
            <Text variant="bodyMd" as="p" fontWeight="medium">
              {t('form.field.displayName.label')}
              {lang !== 'default' && (
                <Text variant="bodySm" as="span" tone="subdued">
                  {' '}{t('form.translated')}
                </Text>
              )}
            </Text>
          </Box>
          <TextField
            label=""
            labelHidden
            value={getTranslated('displayName')}
            onChange={(value) => setTranslated('displayName', value)}
            autoComplete="off"
            placeholder={lang === 'default' ? '' : (formData.displayName || t('form.field.displayName.placeholder'))}
            helpText={lang === 'default'
              ? t('form.field.displayName.help')
              : (hasPreset(lang)
                ? t('form.field.displayName.helpPreset', { lang: langLabel })
                : t('form.field.displayName.helpPresetFallback', { lang: langLabel, primary: primaryLanguageName }))}
            error={errors.displayName}
            requiredIndicator={lang === 'default'}
            maxLength={50}
            showCharacterCount
          />

          <Box paddingBlockStart="200">
            <Text variant="bodyMd" as="p" fontWeight="medium">
              {t('form.field.description.label')}
              {lang !== 'default' && (
                <Text variant="bodySm" as="span" tone="subdued">
                  {' '}{t('form.translated')}
                </Text>
              )}
            </Text>
          </Box>
          <TextField
            label=""
            labelHidden
            value={getTranslated('description')}
            onChange={(value) => setTranslated('description', value)}
            multiline={3}
            maxLength={160}
            showCharacterCount
            autoComplete="off"
            placeholder={lang === 'default' ? '' : (formData.description || t('form.field.description.placeholder'))}
            helpText={t('form.field.description.help')}
          />

          <Box paddingBlockStart="200">
            <Text variant="bodyMd" as="p" fontWeight="medium">
              {t('form.field.disabledDescription.label')}
              {lang !== 'default' && (
                <Text variant="bodySm" as="span" tone="subdued">
                  {' '}{t('form.translated')}
                </Text>
              )}
            </Text>
          </Box>
          <TextField
            label=""
            labelHidden
            value={getTranslated('disabledDescription')}
            onChange={(value) => setTranslated('disabledDescription', value)}
            multiline={2}
            maxLength={160}
            showCharacterCount
            autoComplete="off"
            placeholder={lang === 'default'
              ? t('form.field.disabledDescription.placeholder')
              : (formData.disabledDescription || t('form.field.displayName.placeholder'))}
            helpText={t('form.field.disabledDescription.help')}
          />

          <Box paddingBlockStart="300">
            <Text variant="bodyMd" as="p" fontWeight="medium">{t('form.field.footer.label')}</Text>
          </Box>
          <InlineStack gap="500">
            <RadioButton
              label={t('common.disabled')}
              name="footerEnabled"
              checked={!formData.footerEnabled}
              onChange={() => handleChange('footerEnabled')(false)}
            />
            <RadioButton
              label={t('common.enabled')}
              name="footerEnabled"
              checked={formData.footerEnabled}
              onChange={() => handleChange('footerEnabled')(true)}
            />
          </InlineStack>

          {formData.footerEnabled && (
            <>
              <Box paddingBlockStart="200">
                <Text variant="bodyMd" as="p" fontWeight="medium">
                  {t('form.field.footerText.label')}
                  {lang !== 'default' && (
                    <Text variant="bodySm" as="span" tone="subdued">
                      {' '}{t('form.translated')}
                    </Text>
                  )}
                </Text>
              </Box>
              <TextField
                label=""
                labelHidden
                value={getTranslated('footerText')}
                onChange={(value) => setTranslated('footerText', value)}
                autoComplete="off"
                placeholder={lang === 'default'
                  ? t('form.defaults.viewTerms')
                  : (formData.footerText || t('form.defaults.viewTerms'))}
              />
              <TextField
                label={t('form.field.footerLink.label')}
                value={formData.footerLink}
                onChange={handleChange('footerLink')}
                autoComplete="off"
                placeholder="https://terms.com"
                helpText={t('form.field.footerLink.help')}
              />
            </>
          )}

          {/* defaultEnabled(默认勾选)保留在数据模型里但不暴露 UI:预勾选
              加价项违反 App Store 1.1.9(买家显式同意),只在后端按店铺
              特批时通过 API 开启。表单保存时原值透传,不覆盖。 */}
        </SectionCard>

        <SectionCard title={t('form.section.pricing', { type: (typeChoices(t).find(c => c.value === formData.type) || {}).label || t('form.pricingFallback') })}>
          {/* 允许换行:窄屏(手机 admin)时两个字段整宽上下排,不再互相挤压 */}
          <InlineStack gap="400" wrap>
            <div style={{ flex: 1 }}>
              <Select
                label={t('form.price.type.label')}
                options={PRICE_TYPES.map(pt => ({ ...pt, label: t(`priceType.${pt.value}`) }))}
                value={formData.priceType}
                onChange={handleChange('priceType')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label={formData.priceType === 'fixed' ? t('form.price.fixed') : t('form.price.percentage')}
                type="number"
                min={0}
                step={0.01}
                value={formData.priceValue}
                onChange={handleChange('priceValue')}
                autoComplete="off"
                requiredIndicator
                error={errors.priceValue}
                prefix={formData.priceType === 'fixed' ? '$' : undefined}
                suffix={formData.priceType === 'percentage' ? '%' : undefined}
              />
            </div>
          </InlineStack>

          <Box paddingBlockStart="300">
            <Button
              plain
              icon={PlusCircleIcon}
              onClick={formData.priceType === 'percentage' ? openAdvModal : openTiersModal}
            >
              {t('form.price.advanced')}
            </Button>
            {formData.priceType === 'percentage' && (
              <Box paddingBlockStart="150">
                <Text variant="bodySm" as="p" tone="subdued">
                  {(() => {
                    const min = formData.minCharge != null ? toNumberOr(formData.minCharge, PCT_DEFAULTS.minCharge) : PCT_DEFAULTS.minCharge;
                    const inc = formData.incrementAmount != null ? toNumberOr(formData.incrementAmount, PCT_DEFAULTS.incrementAmount) : PCT_DEFAULTS.incrementAmount;
                    const max = (toNumberOr(min, 0) || 0) + (toNumberOr(inc, 0) || 0) * (PCT_BUCKETS - 1);
                    const pct = toNumberOr(formData.priceValue, 0) || 0;
                    return t('form.price.pctSummary', {
                      pct: pct % 1 === 0 ? pct : pct.toFixed(2),
                      min: formatMoneyInput(min),
                      inc: formatMoneyInput(inc),
                      max: max.toFixed(2),
                      count: PCT_BUCKETS
                    });
                  })()}
                </Text>
              </Box>
            )}
            {formData.priceType !== 'percentage' && formData.pricingTiers !== '[]' && (() => {
              try {
                const arr = JSON.parse(formData.pricingTiers || '[]');
                if (Array.isArray(arr) && arr.length > 0) {
                  return (
                    <Box paddingBlockStart="150">
                      <Text variant="bodySm" as="p" tone="subdued">
                        {tp('form.price.tiersSummary', arr.length, {
                          base: formatMoneyInput(formData.priceValue)
                        })}
                      </Text>
                    </Box>
                  );
                }
              } catch { /* ignore */ }
              return null;
            })()}
          </Box>
        </SectionCard>

        <SectionCard title={t('form.section.appearance')}>
          <Box>
            <Text variant="bodyMd" as="p" fontWeight="medium">{t('form.icon.label')}</Text>
            <Box paddingBlockStart="150">
              <InlineStack gap="300" wrap align="start">
                {ICON_CHOICES.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    aria-pressed={iconKey(formData.icon) === emoji}
                    aria-label={t('form.icon.useEmoji', { emoji })}
                    onClick={() => handleChange('icon')(emoji)}
                    style={iconTileStyle(iconKey(formData.icon) === emoji)}
                  >
                    <img
                      src={PLAN_ICON_IMAGES[emoji]}
                      alt=""
                      width={ICON_IMAGE_SIZE}
                      height={ICON_IMAGE_SIZE}
                      draggable={false}
                      style={{ display: 'block', pointerEvents: 'none' }}
                    />
                  </button>
                ))}
                {isIconUrl(formData.icon) && (
                  <div style={{ ...iconTileStyle(true), position: 'relative' }}>
                    <img
                      src={formData.icon}
                      alt=""
                      width={ICON_IMAGE_SIZE}
                      height={ICON_IMAGE_SIZE}
                      draggable={false}
                      style={{ display: 'block', objectFit: 'contain', pointerEvents: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={removeCustomIcon}
                      aria-label={t('form.icon.removeCustom')}
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        border: '1px solid #D0D0D0',
                        background: '#FFFFFF',
                        color: '#616161',
                        fontSize: 14,
                        lineHeight: '20px',
                        padding: 0,
                        cursor: 'pointer'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
                {uploadFile && (
                  <button
                    type="button"
                    onClick={pickIconFile}
                    disabled={iconUploading}
                    aria-label={t('form.icon.upload')}
                    style={{
                      ...iconTileStyle(false),
                      flexDirection: 'column',
                      gap: 6,
                      borderStyle: 'dashed',
                      color: '#616161',
                      background: '#FAFAFA'
                    }}
                  >
                    {iconUploading ? (
                      <Spinner size="small" accessibilityLabel={t('form.icon.uploading')} />
                    ) : (
                      <>
                        <span style={{ fontSize: 26, lineHeight: '26px' }}>+</span>
                        <span style={{ fontSize: 12 }}>{t('form.icon.uploadButton')}</span>
                      </>
                    )}
                  </button>
                )}
                <input
                  ref={iconFileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  style={{ display: 'none' }}
                  onChange={onIconFileChosen}
                />
              </InlineStack>
              {iconUploadError && (
                <Box paddingBlockStart="200">
                  <InlineError fieldID="plan-icon-upload" message={iconUploadError} />
                </Box>
              )}
              {uploadFile && (
                <Box paddingBlockStart="200">
                  <Text variant="bodySm" as="p" tone="subdued">
                    {t('form.icon.uploadHelp')}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </SectionCard>

        <SectionCard title={t('form.section.fulfillment')}>
          <Text variant="bodyMd" as="p" tone="subdued">
            {t('form.fulfillment.intro')}
          </Text>
          <Box paddingBlockStart="200">
            <ChoiceList
              title=""
              titleHidden
              name="fulfillment"
              choices={[
                { value: 'manually', label: t('form.fulfillment.manually') },
                { value: 'first_item', label: t('form.fulfillment.first_item') },
                { value: 'all_items', label: t('form.fulfillment.all_items') },
                { value: 'immediately', label: t('form.fulfillment.immediately') }
              ]}
              selected={[formData.fulfillment || 'manually']}
              onChange={(selected) => handleChange('fulfillment')(selected[0])}
            />
          </Box>
        </SectionCard>
      </div>

      {/* Percentage bucket pricing */}
      <Modal
        open={advOpen}
        onClose={() => setAdvOpen(false)}
        title={t('form.adv.title')}
        primaryAction={{ content: t('common.save'), disabled: !advValid, onAction: saveAdvModal }}
        secondaryActions={[{ content: t('common.cancel'), onAction: () => setAdvOpen(false) }]}
      >
        <Modal.Section>
          <TextField
            label={t('form.adv.minPrice')}
            type="number"
            min={0}
            step={0.01}
            suffix="$"
            value={stagedAdv.minCharge}
            onChange={(value) => setStagedAdv(s => ({ ...s, minCharge: value }))}
            autoComplete="off"
            helpText={t('form.adv.minPriceHelp')}
          />
        </Modal.Section>
        <Modal.Section>
          <TextField
            label={t('form.adv.increment')}
            type="number"
            min={0}
            step={0.01}
            suffix="$"
            value={stagedAdv.incrementAmount}
            onChange={(value) => setStagedAdv(s => ({ ...s, incrementAmount: value }))}
            autoComplete="off"
            helpText={t('form.adv.incrementHelp')}
          />
          {advValid && (
            <Box paddingBlockStart="200">
              <Text variant="bodyMd" as="p">
                {t('form.adv.maxPriceLabel')}<Text variant="bodyMd" as="span" fontWeight="bold">${advMax}</Text>
              </Text>
            </Box>
          )}
          <Box paddingBlockStart="200">
            <Text variant="bodySm" as="p" tone="subdued">
              {t('form.adv.explanation', {
                buckets: PCT_BUCKETS,
                min: `$${advMin != null && advMin > 0 ? formatMoneyInput(advMin) : '0.00'}`,
                rest: PCT_BUCKETS - 1,
                inc: `$${advInc != null && advInc > 0 ? formatMoneyInput(advInc) : '0.00'}`,
                max: `$${advMax || '0.00'}`,
                pct: advPct % 1 === 0 ? advPct : (advPct || 0).toFixed(2)
              })}{' '}
              <span style={{ color: '#005BD3', cursor: 'pointer' }}>{t('form.learnMore')}</span>
            </Text>
          </Box>
        </Modal.Section>
      </Modal>

      {/* Fixed price tiers by cart value */}
      <Modal
        open={tiersOpen}
        onClose={() => setTiersOpen(false)}
        title={t('form.tiers.title')}
        primaryAction={{ content: t('common.save'), disabled: !tiersValid, onAction: saveTiersModal }}
        secondaryActions={[{ content: t('common.cancel'), onAction: () => setTiersOpen(false) }]}
      >
        <Modal.Section>
          {stagedTiers.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 32px',
                gap: '8px',
                alignItems: 'end',
                marginBottom: '4px'
              }}
            >
              <Text variant="bodySm" as="p" fontWeight="medium">{t('form.tiers.minCol')}</Text>
              <Text variant="bodySm" as="p" fontWeight="medium">{t('form.tiers.maxCol')}</Text>
              <Text variant="bodySm" as="p" fontWeight="medium">{t('form.tiers.priceCol')}</Text>
              <span />
            </div>
          )}
          {stagedTiers.map((tier, index) => (
            <div key={index}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 32px',
                  gap: '8px',
                  alignItems: 'center',
                  paddingBlock: '6px'
                }}
              >
                <TextField
                  label=""
                  labelHidden
                  type="number"
                  min={0}
                  step={0.01}
                  prefix="$"
                  value={tier.min}
                  onChange={(value) => updateTier(index, 'min', value)}
                  autoComplete="off"
                  error={tierErrors[index] ? ' ' : undefined}
                />
                <TextField
                  label=""
                  labelHidden
                  type="number"
                  min={0}
                  step={0.01}
                  prefix="$"
                  value={tier.max}
                  onChange={(value) => updateTier(index, 'max', value)}
                  autoComplete="off"
                  error={tierErrors[index] ? ' ' : undefined}
                />
                <TextField
                  label=""
                  labelHidden
                  type="number"
                  min={0}
                  step={0.01}
                  prefix="$"
                  value={tier.price}
                  onChange={(value) => updateTier(index, 'price', value)}
                  autoComplete="off"
                  error={tierErrors[index] ? ' ' : undefined}
                />
                <Button
                  icon={DeleteIcon}
                  onClick={() => removeTier(index)}
                  accessibilityLabel={t('form.tiers.deleteTier', { index: index + 1 })}
                />
              </div>
              {tierErrors[index] && (
                <Box paddingBlockStart="050">
                  <Text variant="bodySm" as="p" tone="critical">{tierErrors[index]}</Text>
                </Box>
              )}
            </div>
          ))}
          <Box paddingBlockStart="200">
            <Button onClick={addTier}>{t('form.tiers.addTier')}</Button>
          </Box>
          <Box paddingBlockStart="300">
            <Text variant="bodySm" as="p" tone="subdued">
              {t('form.tiers.explanation')}{' '}
              <span style={{ color: '#005BD3', cursor: 'pointer' }}>{t('form.learnMore')}</span>
            </Text>
          </Box>
        </Modal.Section>
      </Modal>
    </Form>
  );
});

export default ProtectionPlanForm;
